import { useGetUserListById } from "@synthesis/shared-services/auth/user/hooks";
import { list as listSupportingDocuments } from "@synthesis/shared-services/files/supporting-document/client";
import type { FileMetaDto, SupportingDocumentListResponse } from "@synthesis/shared-services/files/supporting-document/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

export const REVIEW_DOCUMENTS_QUERY_KEY = (requestId: string) => ["review-documents", requestId] as const;

export type ReviewDocument = {
  id: string;
  fileName: string;
  baseName: string;
  ext?: string | null;
  createdAt: string;
  authorId: string;
  isDeleted: boolean;
};

export type ReviewDocumentAuthor = {
  id: string;
  name: string;
};

const formatFileName = (meta: Pick<FileMetaDto, "baseName" | "ext">): string => (meta.ext ? `${meta.baseName}.${meta.ext}` : meta.baseName);

const toReviewDocument = (item: SupportingDocumentListResponse): ReviewDocument | null => {
  const meta = item.fileMeta;

  if (!meta) {
    return null;
  }

  return {
    id: meta.id,
    fileName: formatFileName(meta),
    baseName: meta.baseName,
    ext: meta.ext,
    createdAt: meta.createdAt,
    authorId: meta.author,
    isDeleted: meta.isDeleted,
  };
};

const formatUserDtoName = (user: { id?: string | null; lastName?: string | null; firstName?: string | null; middleName?: string | null }): string => {
  const name = [user.lastName, user.firstName, user.middleName].filter(Boolean).join(" ");

  return name || "—";
};

export const useReviewDocuments = (requestId: string) => {
  const queryClient = useQueryClient();

  const documentsQuery = useQuery({
    queryKey: REVIEW_DOCUMENTS_QUERY_KEY(requestId),
    queryFn: async ({ signal }) => (await listSupportingDocuments({ body: { id: requestId }, signal })).data,
    enabled: Boolean(requestId),
  });

  const documents = useMemo<ReviewDocument[]>(
    () => (documentsQuery.data ?? []).map(toReviewDocument).filter((item): item is ReviewDocument => item !== null && !item?.isDeleted),
    [documentsQuery.data],
  );

  const authorIds = useMemo(() => Array.from(new Set(documents.map((document) => document.authorId).filter(Boolean))), [documents]);

  const authorsQuery = useGetUserListById(
    { query: { userIds: authorIds } },
    {
      query: { enabled: authorIds.length > 0 },
    },
  );

  const authorsMap = useMemo<Map<string, ReviewDocumentAuthor>>(() => {
    const map = new Map<string, ReviewDocumentAuthor>();
    const users = authorsQuery.data ?? [];

    for (const user of users) {
      const id = user.id;

      if (!id) {
        continue;
      }

      map.set(id, { id, name: formatUserDtoName(user) });
    }

    return map;
  }, [authorsQuery.data]);

  const invalidateReviewDocuments = () => queryClient.invalidateQueries({ queryKey: REVIEW_DOCUMENTS_QUERY_KEY(requestId) });

  return {
    documents,
    authorsMap,
    isLoading: documentsQuery.isLoading,
    isError: documentsQuery.isError,
    invalidateReviewDocuments,
  };
};
